import { useRef, useEffect } from 'react';
import {
    ApplicantContainer,
    DisclosureDataStore,
    DisclosuresContainer,
    ApplicationDataStore,
    ImperativeForm,
} from '@servicetitan/financing-application-shared';
import { WorkflowStore } from '@servicetitan/financing-workflow-shared';
import { FinancedAmountStore } from '@servicetitan/financing-plans-shared';
import { applicationPanel } from './ApplicationWorkflow.module.scss';
import { StepperLayout } from '../layout';
import { PrequalificationButtons } from './PrequalificationButtons';

interface SecondLookApplicationWorkflowProps {
    useCustomButtons?: boolean;
}

export function SecondLookApplicationWorkflow({
    useCustomButtons = false,
}: SecondLookApplicationWorkflowProps) {
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { disclosureState } = DisclosureDataStore.useStore();
    const { agreedToDisclosures } = disclosureState;
    const { setApplicationData } = ApplicationDataStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const formRef = useRef<ImperativeForm>(null);

    // Set the requested loan amount from financed amount when component mounts
    useEffect(() => {
        if (financedAmount > 0) {
            setApplicationData(prev => {
                // Safely initialize nested objects to prevent runtime errors
                const updatedApplicationData = { ...prev };

                if (!updatedApplicationData.applicant) {
                    updatedApplicationData.applicant = {};
                }
                updatedApplicationData.applicant.primaryApplicant ??= {};

                // Now safely set the requested loan amount
                updatedApplicationData.applicant.primaryApplicant.requestedLoanAmount =
                    financedAmount;

                return updatedApplicationData;
            });
        }
    }, [financedAmount, setApplicationData]);

    const steps = [
        {
            name: 'applicant',
            label: 'Applicant',
            panelClassName: applicationPanel,
            panelComponent: <ApplicantContainer formRef={formRef} />,
        },
        {
            name: 'disclosures',
            label: 'Disclosures',
            panelClassName: applicationPanel,
            panelComponent: <DisclosuresContainer />,
            buttonsComponent: (
                <PrequalificationButtons
                    formRef={formRef}
                    isNextButtonDisabled={!agreedToDisclosures}
                    customFinalStepTitle={useCustomButtons ? 'Submit Application' : undefined}
                />
            ),
        },
    ];

    return (
        <StepperLayout
            onComplete={() => setWorkflowStage('second-look-submitting')}
            buttonsComponent={<PrequalificationButtons formRef={formRef} />}
            steps={steps}
        />
    );
}
